import type { I18nProps } from 'app.types';
import cn from 'classnames';
import { ProcessingPageAbstract } from 'containers/payments/ProcessingPageAbstract';
import { ProductsListAbstract } from 'containers/payments/ProductsListAbstract';
import { newPromoSpecialService } from 'domains/pages/promo-special-new';
import { getDiscountSelector } from 'domains/products/products.selectors';
import type { GetServerSideProps, GetServerSidePropsResult } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import dayjs from 'lib/dayjs-setup';

import { setUserAgent, setRegion } from 'redux-store/slices/ui';

import Timer from 'components/domains/common/Timer';
import TimerBetween from 'components/domains/common/TimerBetween';
import { DiscountBadge } from 'components/domains/payments/DiscountBadge';
import type { ProductProps } from 'components/domains/payments/Product';
import { Product } from 'components/domains/payments/Product';
import { H2 } from 'components/domains/typography/Typography';
import Page from 'components/layouts/Page';

import * as productSelectors from 'domains/products/products.selectors';
import type { ProductsTypeV3 } from 'domains/products/products.types';

import type { AppState } from 'redux-store';
import { useAppSelector, wrapper } from 'redux-store';

import { startSpanAndSetTransaction } from 'utils/APMService';
import getTranslate from 'utils/getTranslate';

import stylesExperiment from './experimentDefault.module.scss';
import styles from './styles.module.scss';

const screen = 'promo-special';

// eslint-disable-next-line complexity
const PremiumFeaturesPage: React.FC = () => {
  const router = useRouter();
  const [showTimer, setSowTimer] = useState(false);
  const [timerEnd, setTimerEnd] = useState(false);
  
  const startDate = useSelector(
    (state: AppState) => state.newPromoSpecial.start,
  );
  const { userAgent } = useAppSelector(state => state.ui);

  const isMobile = userAgent.device.type === 'mobile';
  const coupon = router.query.coupon as string | undefined;
  const timerQuery = router.query.timer as string | undefined;
  const timer24Experiment = timerQuery === '24h' && coupon;
  const isNewPaymentButton = !!router.query.new_button;
  const selectedIdQuery = router.query.selected as string | undefined;

  let productsSelector = productSelectors.getProductsSelector;

  const products = useSelector(productsSelector);

  const selectedProduct = selectedIdQuery
    ? products.find((product) => product.id === +selectedIdQuery)
    : undefined;
  const defaultProduct = selectedIdQuery && selectedProduct ? selectedProduct : products[0];

  const [activeProduct, setActiveProduct] = useState(defaultProduct);

  let title = (
    <H2 className={styles.center}>
      Your special offer <br /> on Welltory Premium
    </H2>
  );
  let timer = null;
  const futureTimerDate = dayjs(timerQuery || null);
  const timerExperiment = futureTimerDate.isValid() && !!coupon;

  useEffect(() => {
    setSowTimer(true);
  }, []);

  const onChangeActiveProduct = (product: ProductsTypeV3) => {
    setActiveProduct(product);
  }

  if (timer24Experiment && isMobile && showTimer) {
    timer = (
      <Timer
        startDate={startDate}
        className={stylesExperiment.timer}
        onEnd={(): void => setTimerEnd(true)}
      />
    );
  }
  else if (timerExperiment && isMobile && showTimer) {
    timer = (
      <TimerBetween
        endDate={futureTimerDate.toString()}
        className={stylesExperiment.timer}
        onEnd={(): void => setTimerEnd(true)}
        removeAfterFinish
      />
    );
  }


  const extraProps = useMemo(
    () => ({
      hideDiscount: false
    }),
    [
      isMobile,
    ],
  );

  const paymentNextButtonText = isNewPaymentButton === false ? 'pay with card' : activeProduct.prices[0].interval === 'lifetime' ? 'Get Lifetime Offer' : 'Get Annual Offer'

  return (
    <Page withoutFooter fullWidth className={styles.page}>
      <ProcessingPageAbstract
        screen={screen}
        className={cn(styles.paymentPromoSpecialForm, {[styles.newPaymentButton]: isNewPaymentButton === true })}
        nextButtonText={paymentNextButtonText}
        cardButtonText="pay with card"
      >
        <div className={styles.logo}>
          <div className={styles.logoContent}>
            <Image
              src="/pagesImages/premium-features/logo.png"
              width={188}
              height={88}
            />
          </div>
        </div>
        {timer && 
          <div className={stylesExperiment.timerBlock}>
            <div>{timer}</div>
            {!timerEnd ? <div>Limited-time offer</div> : null}
          </div>
        }
        <div
          className={cn(styles.container, {
            [stylesExperiment.experimentTimer]: timerQuery,
          })}
        >
          {title}
          <ProductsListAbstract<ProductProps>
            component={Product}
            extra={extraProps}
            productsSelector={productsSelector}
            onChange={onChangeActiveProduct}
          />
        </div>
      </ProcessingPageAbstract>
    </Page>
  );
};

export default PremiumFeaturesPage;

export const getServerSideProps: GetServerSideProps =
  wrapper.getServerSideProps(
    store =>
      async (context): Promise<GetServerSidePropsResult<I18nProps>> => {
        const endSpan = startSpanAndSetTransaction(context.resolvedUrl);

        await newPromoSpecialService.server({ context, store });

        await store.dispatch(setUserAgent(context.req.headers['user-agent']));

        const region = (context.req.headers['cf-ipcountry'] as string) || '';
        if (region) {
          await store.dispatch(setRegion(region));
        }

        endSpan();

        return {
          props: {
            ...(await getTranslate(context)),
          },
        };
      },
  );
